import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CheckBoxInterface,
  ConditionRowInterface,
  FilterSidebarProps,
  PrimaryCategoryInterface,
  SelectedFilters,
  SubCategoryInterface,
  TertiaryCategoryInterface,
} from '../interface/interface';
import {
  ArrowRightIcon,
  CloseIcon,
  Minus,
  NavigationCarat,
  Plus,
  SearchIcon,
  Slider,
} from '../assets/svgImages';
import {
  buildBrandList,
  buildCategoryTree,
  filterCategoryTree,
  saveSelectedCategories,
} from '../utils/productCategoryTree';

import {
  CATEGORY_KEY_SEPARATOR,
  EMPTY_FILTERS,
  SELECTED_CATEGORIES_KEY,
} from '../constant/constant';

function Checkbox({ checked, onChange }: Readonly<CheckBoxInterface>) {
  return (
    <div className="flex justify-center">
      <input
        type="checkbox"
        className={`shrink-0 flex items-center justify-center rounded border-2 transition-colors cursor-pointer !w-5 !h-5
        ${
          checked
            ? '!bg-primary-blue-500 border-primary-blue-500'
            : 'bg-white border-gray-300'
        }`}
        style={{ width: 13, height: 13 }}
        checked={checked}
        onChange={onChange}
      />
    </div>
  );
}

function CountPill({ count }: Readonly<{ count: number }>) {
  return (
    <span className="ml-auto hidden text-xs font-medium text-primary-blue-500 bg-gray-100 rounded-full px-2.5 py-1 min-w-[2.25rem] text-center">
      {count}
    </span>
  );
}

function EmptyState({ text }: { text: string }) {
  return <p className="text-sm text-gray-400 py-3">{text}</p>;
}

function TertiaryCategoryRow({
  node,
  isChecked,
  onToggle,
}: Readonly<TertiaryCategoryInterface>) {
  return (
    <div className="flex items-center gap-2 py-3 rounded-md">
      <span className="text-gray-300 text-xs">
        <ArrowRightIcon className="w-3 h-3" />
      </span>

      <Checkbox checked={isChecked} onChange={onToggle} />

      <button
        type="button"
        onClick={onToggle}
        className={`text-sm text-left ${
          isChecked ? 'font-semibold text-primary-blue-500' : 'text-gray-800'
        }`}
      >
        {node.label}
      </button>

      <CountPill count={node.count} />
    </div>
  );
}

function SubCategoryRow({
  node,
  isChecked,
  isExpanded,
  checkedTertiaryKeys,
  onToggleChecked,
  onToggleExpanded,
  onToggleTertiaryChecked,
}: Readonly<SubCategoryInterface>) {
  const hasTertiary = node.tertiaryCategories.length > 0;

  return (
    <div>
      <div className="flex items-center gap-2 py-3 rounded-md">
        <button
          type="button"
          onClick={onToggleExpanded}
          className="text-gray-300 text-xs shrink-0"
          aria-label={`Toggle ${node.label}`}
        >
          <ArrowRightIcon className="w-3 h-3" />
        </button>

        <Checkbox checked={isChecked} onChange={onToggleChecked} />

        <div className="flex-1 flex items-center gap-1">
          <button
            type="button"
            onClick={onToggleExpanded}
            className={`text-sm text-left ${
              isChecked
                ? 'font-semibold text-primary-blue-500'
                : 'text-gray-800'
            }`}
          >
            {node.label}
          </button>

          {hasTertiary && (
            <button
              type="button"
              onClick={onToggleExpanded}
              aria-label={`Toggle ${node.label}`}
              aria-expanded={isExpanded}
            >
              <NavigationCarat
                className={`w-3 h-3 text-gray-400 transition-transform ${
                  isExpanded ? 'rotate-90' : ''
                }`}
              />
            </button>
          )}
        </div>

        <CountPill count={node.count} />
      </div>

      {isExpanded && hasTertiary && (
        <div className="ml-6 border-l border-dashed border-gray-300 pl-3">
          {node.tertiaryCategories.map((tertiary) => (
            <TertiaryCategoryRow
              key={tertiary.key}
              node={tertiary}
              isChecked={checkedTertiaryKeys.includes(tertiary.key)}
              onToggle={() => onToggleTertiaryChecked(tertiary.key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PrimaryCategoryRow({
  node,
  isChecked,
  isExpanded,
  checkedSubKeys,
  checkedTertiaryKeys,
  expandedSubKeys,
  forceExpandSubs,
  onToggleChecked,
  onToggleExpanded,
  onToggleSubChecked,
  onToggleSubExpanded,
  onToggleTertiaryChecked,
}: Readonly<PrimaryCategoryInterface>) {
  const hasSubCategories = node.subCategories.length > 0;

  return (
    <div>
      <div className="flex items-center gap-2 py-3 rounded-md">
        <Checkbox checked={isChecked} onChange={onToggleChecked} />

        <button
          type="button"
          onClick={onToggleExpanded}
          className={`text-sm text-left min-w-[85%] ${
            isChecked
              ? 'font-semibold text-primary-blue-500'
              : 'text-primary-blue-500'
          }`}
        >
          {node.label}
        </button>
        {hasSubCategories ? (
          <button
            type="button"
            onClick={onToggleExpanded}
            aria-expanded={isExpanded}
            className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 text-primary-blue-500 shrink-0 justify-self-end"
            aria-label={`Toggle ${node.label}`}
          >
            {isExpanded ? (
              <Minus className="w-3 h-3" />
            ) : (
              <Plus className="w-3 h-3" />
            )}
          </button>
        ) : (
          <span className="w-5 h-5 shrink-0" />
        )}

        <CountPill count={node.count} />
      </div>

      {isExpanded && hasSubCategories && (
        <div className="ml-2 border-l border-dashed border-gray-300 pl-3">
          {node.subCategories.map((sub) => {
            const isSubChecked = checkedSubKeys.includes(sub.key);

            return (
              <SubCategoryRow
                key={sub.key}
                node={sub}
                isChecked={isSubChecked}
                isExpanded={
                  forceExpandSubs || expandedSubKeys.includes(sub.key)
                }
                checkedTertiaryKeys={checkedTertiaryKeys}
                onToggleChecked={() => onToggleSubChecked(sub.key)}
                onToggleExpanded={() => onToggleSubExpanded(sub.key)}
                onToggleTertiaryChecked={onToggleTertiaryChecked}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function ConditionRow({ node, isChecked, onToggle }: ConditionRowInterface) {
  return (
    <div className="flex items-center gap-2 py-2.5">
      <Checkbox checked={isChecked} onChange={onToggle} />

      <button
        type="button"
        onClick={onToggle}
        className={`text-sm text-left text-primary-blue-500`}
      >
        {node.label}
      </button>

      <CountPill count={node.count} />
    </div>
  );
}

export default function FilterSidebar({
  onFilterChange,
  allProducts,
  selectedFilters,
}: Readonly<FilterSidebarProps>) {
  const [categorySearch, setCategorySearch] = useState('');
  const [healthConcernsOpen, setHealthConcernsOpen] = useState(true);
  const [brandsOpen, setBrandsOpen] = useState(true);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const appliedFilters = selectedFilters ?? EMPTY_FILTERS;

  const [draftFilters, setDraftFilters] =
    useState<SelectedFilters>(appliedFilters);

  const selected = isMobileOpen ? draftFilters : appliedFilters;

  const [expandedPrimaries, setExpandedPrimaries] = useState<string[]>(() => [
    ...new Set([
      ...selected.primaryCategories,
      ...[...selected.subCategories, ...selected.tertiaryCategories].map(
        (key) => key.split(CATEGORY_KEY_SEPARATOR)[0],
      ),
    ]),
  ]);
  const [expandedSubs, setExpandedSubs] = useState<string[]>(() => [
    ...new Set([
      ...selected.subCategories,
      ...selected.tertiaryCategories.map((key) =>
        key
          .split(CATEGORY_KEY_SEPARATOR)
          .slice(0, 2)
          .join(CATEGORY_KEY_SEPARATOR),
      ),
    ]),
  ]);

  const location = useLocation();

  useEffect(() => {
    const primaryCategory = location.state?.primaryCategory;
    if (!primaryCategory) return;
    setCategorySearch('');
    setExpandedPrimaries([primaryCategory]);
    setExpandedSubs([]);
  }, [location.key]);

  const updateSelected = (
    update: (prev: SelectedFilters) => SelectedFilters,
  ) => {
    const next = update(selected);
    saveSelectedCategories(next);

    if (isMobileOpen) {
      setDraftFilters(next);
    } else {
      onFilterChange(next);
    }
  };

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  const categoryTree = buildCategoryTree(allProducts);

  const brandList = buildBrandList(allProducts);

  const filteredCategoryTree = filterCategoryTree(categoryTree, categorySearch);

  const isSearching = categorySearch.trim().length > 0;

  const toggleListEntry = (list: string[], value: string) =>
    list.includes(value)
      ? list.filter((item) => item !== value)
      : [...list, value];

  const isDescendantOf = (key: string, ancestor: string) =>
    key.startsWith(ancestor + CATEGORY_KEY_SEPARATOR);

  const removeDescendants = (list: string[], ancestor: string) =>
    list.filter((key) => !isDescendantOf(key, ancestor));

  const togglePrimaryExpanded = (label: string) =>
    setExpandedPrimaries((prev) => toggleListEntry(prev, label));

  const toggleSubExpanded = (key: string) =>
    setExpandedSubs((prev) => toggleListEntry(prev, key));

  const togglePrimaryChecked = (label: string) => {
    const isUnchecking = selected.primaryCategories.includes(label);

    setExpandedPrimaries((prev) =>
      isUnchecking
        ? prev.filter((item) => item !== label)
        : prev.includes(label)
          ? prev
          : [...prev, label],
    );
    if (isUnchecking) {
      setExpandedSubs((prev) => removeDescendants(prev, label));
    }

    updateSelected((prev) => {
      return {
        ...prev,
        primaryCategories: toggleListEntry(prev.primaryCategories, label),
        subCategories: isUnchecking
          ? removeDescendants(prev.subCategories, label)
          : prev.subCategories,
        tertiaryCategories: isUnchecking
          ? removeDescendants(prev.tertiaryCategories, label)
          : prev.tertiaryCategories,
      };
    });
  };

  const toggleSubChecked = (key: string) => {
    const isUnchecking = selected.subCategories.includes(key);

    setExpandedSubs((prev) =>
      isUnchecking
        ? prev.filter((item) => item !== key)
        : prev.includes(key)
          ? prev
          : [...prev, key],
    );

    updateSelected((prev) => {
      return {
        ...prev,
        subCategories: toggleListEntry(prev.subCategories, key),
        tertiaryCategories: isUnchecking
          ? removeDescendants(prev.tertiaryCategories, key)
          : prev.tertiaryCategories,
      };
    });
  };

  const toggleTertiaryChecked = (key: string) => {
    updateSelected((prev) => ({
      ...prev,
      tertiaryCategories: toggleListEntry(prev.tertiaryCategories, key),
    }));
  };

  const toggleBrandChecked = (label: string) => {
    updateSelected((prev) => ({
      ...prev,
      brands: toggleListEntry(prev.brands ?? [], label),
    }));
  };

  const handleClear = () => {
    localStorage.removeItem(SELECTED_CATEGORIES_KEY);
    if (isMobileOpen) {
      setDraftFilters(EMPTY_FILTERS);
      onFilterChange(EMPTY_FILTERS);
    } else {
      onFilterChange(EMPTY_FILTERS);
    }
    setExpandedPrimaries([]);
    setExpandedSubs([]);
  };

  const openMobileFilters = () => {
    setDraftFilters(appliedFilters);
    setIsMobileOpen(true);
  };

  const handleMobileApply = () => {
    onFilterChange(draftFilters);
    setIsMobileOpen(false);
  };

  const countFilters = (filters: SelectedFilters) =>
    filters.primaryCategories.length +
    filters.subCategories.length +
    filters.tertiaryCategories.length +
    filters.conditions.length +
    (filters.brands?.length ?? 0);

  const activeCount = countFilters(selected);

  const appliedCount = countFilters(appliedFilters);

  const filterContent = (
    <>
      <div className="flex items-center justify-between px-6 pt-6 pb-4">
        <h2 className="text-sm font-bold text-primary-blue-500 tracking-wide">
          FILTERS
        </h2>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleClear}
            disabled={activeCount === 0}
            className="text-xs font-semibold text-primary-blue-500 hover:text-primary-blue-800 underline
              disabled:text-gray-300 disabled:no-underline disabled:cursor-not-allowed xl:block hidden"
          >
            Clear All
          </button>
          <CloseIcon
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className="cursor-pointer fill-primary-blue-500 sm:w-4 sm:h-4 block xl:hidden"
          />
        </div>
      </div>

      <div className="px-6 pb-4">
        <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3">
          <SearchIcon className="w-4 h-4 text-gray-100 shrink-0" />

          <input
            type="text"
            value={categorySearch}
            onChange={(e) => setCategorySearch(e.target.value)}
            placeholder="Search category..."
            className="w-full text-sm text-gray-700 placeholder:text-gray-400 outline-none bg-transparent border-none"
          />
        </div>
      </div>

      <div className="px-6 pb-2 border-t border-gray-100 pt-2">
        <button
          type="button"
          onClick={() => setBrandsOpen((open) => !open)}
          className="w-full flex items-center justify-between py-2 text-sm font-semibold text-primary-blue-500 tracking-wide"
        >
          Shop by Brand
          <NavigationCarat
            className={`w-4 h-4 text-primary-blue-500 ${
              brandsOpen ? 'rotate-90' : ''
            }`}
          />
        </button>

        {brandsOpen && (
          <div className="pt-1 pb-2">
            {brandList.map((brand) => (
              <ConditionRow
                key={brand.label}
                node={brand}
                isChecked={selected.brands?.includes(brand.label) ?? false}
                onToggle={() => toggleBrandChecked(brand.label)}
              />
            ))}

            {brandList.length === 0 && <EmptyState text="No brands found." />}
          </div>
        )}
      </div>

      <div className="px-6 pb-2">
        <button
          type="button"
          onClick={() => setHealthConcernsOpen((open) => !open)}
          className="w-full flex items-center justify-between py-2 text-sm font-semibold text-primary-blue-500 tracking-wide"
        >
          Shop by Category
          <NavigationCarat
            className={`w-4 h-4 text-primary-blue-500 ${
              healthConcernsOpen ? 'rotate-90' : ''
            }`}
          />
        </button>

        {healthConcernsOpen && (
          <div className="pt-1 pb-2">
            {filteredCategoryTree.map((primary) => {
              const isPrimaryChecked = selected.primaryCategories.includes(
                primary.label,
              );

              return (
                <PrimaryCategoryRow
                  key={primary.label}
                  node={primary}
                  isChecked={isPrimaryChecked}
                  isExpanded={
                    isSearching || expandedPrimaries.includes(primary.label)
                  }
                  checkedSubKeys={selected.subCategories}
                  checkedTertiaryKeys={selected.tertiaryCategories}
                  expandedSubKeys={expandedSubs}
                  forceExpandSubs={isSearching}
                  onToggleChecked={() => togglePrimaryChecked(primary.label)}
                  onToggleExpanded={() => togglePrimaryExpanded(primary.label)}
                  onToggleSubChecked={toggleSubChecked}
                  onToggleSubExpanded={toggleSubExpanded}
                  onToggleTertiaryChecked={toggleTertiaryChecked}
                />
              );
            })}

            {filteredCategoryTree.length === 0 && (
              <EmptyState text="No matching categories." />
            )}
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      <div
        onClick={openMobileFilters}
        className="xl:hidden flex items-center justify-center gap-3 px-5 py-2.5 bg-white rounded-full shadow-sm text-sm font-semibold text-primary-blue-500 hover:shadow-md transition-shadow border border-grey-300"
      >
        <Slider className="fill-primary-blue-500 w-4 h-4 rotate-90" />

        <p>Filters</p>

        {appliedCount > 0 && (
          <span className="ml-1 bg-primary-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {appliedCount}
          </span>
        )}
      </div>

      <div className="hidden xl:block w-full max-w-sm bg-white rounded-lg border border-grey-200 overflow-hidden shadow-normal">
        {filterContent}
      </div>

      {isMobileOpen && (
        <div
          className={`${
            localStorage.getItem('impersonateUser') ? 'top-48' : 'top-32'
          } xl:hidden fixed inset-x-0 bottom-0 z-50 bg-white flex flex-col sm:z-[99]`}
        >
          <div className="flex-1 overflow-y-auto">{filterContent}</div>

          <div className="border-t border-gray-100 p-4 flex justify-center gap-3">
            <button
              type="button"
              onClick={handleClear}
              disabled={activeCount === 0}
              className="border border-primary-blue-500 text-primary-blue-500 text-sm font-semibold px-5 py-2 rounded-lg
                disabled:border-gray-300 disabled:text-gray-300 disabled:cursor-not-allowed"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={handleMobileApply}
              className="bg-primary-blue-500 text-white text-sm font-semibold px-5 py-2 rounded-lg"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </>
  );
}
